/**
 * MeasureGrid — handoff §3.6, the workhorse.
 *
 * Renders every active Quality measure with: submit toggle, code, flags
 * (high-priority ★, topped-out T, issue !), name, denominator counts, met
 * count, not-submitted, exception, performance rate, decile pill, benchmark
 * band, gap analysis with top providers, points, and editable goal.
 *
 * Plus a footer rollup row showing total deciles across selected measures
 * and total Quality category points.
 *
 * Per handoff §9, when `hasSnapshot` is false the grid degrades — the
 * decile/points cells show "—" rather than 0.
 */
import { useMemo } from "react";
import { Widget } from "@tensaw/design-system";
import { SchemaDataGrid, type SchemaDataGridColumn } from "@tensaw/composition";
import type { MeasureRow, RollupSummary } from "@/api/models";
import { formatPercent, formatPoints } from "@/lib/format";
import { SubmitToggleCell } from "./cells/SubmitToggleCell";
import { DecileCell } from "./cells/DecileCell";
import { GapAnalysisCell } from "./cells/GapAnalysisCell";
import { GoalCell } from "./cells/GoalCell";
import type { PinnedContext } from "./types";

export interface MeasureGridProps {
  clinicId: number;
  year: number;
  measures: MeasureRow[];
  rollup: RollupSummary;
  hasSnapshot: boolean;
  pinned: PinnedContext | null;
}

function FlagsCell({ row }: { row: MeasureRow }) {
  return (
    <div className="flex items-center gap-1 text-xs">
      {row.is_high_priority ? (
        <span title="High priority" className="text-amber-500" aria-label="High priority">★</span>
      ) : null}
      {row.is_topped_out ? (
        <span title="Topped out (max 7 pts)" className="rounded border border-amber-300 bg-amber-50 px-1 font-semibold text-amber-700" aria-label="Topped out">T</span>
      ) : null}
      {row.is_inverse ? (
        <span title="Inverse measure — lower performance rate is better" className="rounded border border-sky-300 bg-sky-50 px-1 font-semibold text-sky-700" aria-label="Inverse measure">↓</span>
      ) : null}
      {row.no_benchmark_reason ? (
        <span title={row.no_benchmark_reason} className="text-red-500" aria-label="Issue">!</span>
      ) : null}
    </div>
  );
}

function BenchmarkCell({ row }: { row: MeasureRow }) {
  const b = row.benchmark;
  if (!b) return <span className="text-gray-400">—</span>;
  const currentMax = b.current_max_rate;
  const nextMin = b.next_min_rate;
  if (currentMax === null || currentMax === undefined) {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <div className="leading-tight">
      <div className="text-xs text-gray-500">D{b.current_decile} cap: {formatPercent(currentMax)}</div>
      {b.next_decile && nextMin !== null && nextMin !== undefined ? (
        <div className="text-xs text-gray-400">D{b.next_decile} min: {formatPercent(nextMin)}</div>
      ) : null}
    </div>
  );
}

export function MeasureGrid({
  clinicId,
  year,
  measures,
  rollup,
  hasSnapshot,
  pinned,
}: MeasureGridProps) {
  // -- Hooks at top, conditional returns below (Tensaw architecture rule §5.2) --
  const columns = useMemo<SchemaDataGridColumn<MeasureRow>[]>(
    () => [
      {
        id: "submit",
        header: "Submit",
        width: 64,
        align: "center",
        cell: ({ row }) => (
          <SubmitToggleCell
            clinicId={clinicId}
            year={year}
            measureId={row.measure_id}
            isSelected={row.is_selected_for_submit}
            pinned={pinned}
          />
        ),
      },
      {
        id: "code",
        header: "Code",
        width: 72,
        align: "left",
        cell: ({ row }) => <span className="tnum text-xs font-semibold text-gray-700">{row.act_measure_id}</span>,
      },
      {
        id: "flags",
        header: "",
        width: 56,
        cell: ({ row }) => <FlagsCell row={row} />,
      },
      {
        id: "measure",
        header: "Measure",
        minWidth: 220,
        cell: ({ row }) => (
          <div className="leading-tight">
            <div className="text-sm font-medium text-gray-800">{row.name}</div>
            <div className="text-xs text-gray-500">
              {row.category}
              {row.has_seven_point_cap ? " · 7-pt cap" : ""}
            </div>
          </div>
        ),
      },
      {
        id: "den_plus_excl",
        header: "DEN+EXCL",
        width: 96,
        align: "right",
        cell: ({ row }) => (
          <span className="num-cell text-sm">{row.current_counts.denominator_plus_excl}</span>
        ),
      },
      {
        id: "met",
        header: "MET",
        width: 72,
        align: "right",
        cell: ({ row }) => <span className="num-cell text-sm">{row.current_counts.met}</span>,
      },
      {
        id: "not_sub",
        header: "NOT SUB",
        width: 88,
        align: "right",
        cell: ({ row }) => <span className="num-cell text-sm">{row.current_counts.not_submitted}</span>,
      },
      {
        id: "exc",
        header: "EXC",
        width: 64,
        align: "right",
        cell: ({ row }) => <span className="num-cell text-sm">{row.current_counts.exception}</span>,
      },
      {
        id: "not_met",
        header: "NOT MET",
        width: 88,
        align: "right",
        cell: ({ row }) => <span className="num-cell text-sm">{row.current_counts.not_met}</span>,
      },
      {
        id: "dc_rate",
        header: "DC %",
        width: 72,
        align: "right",
        cell: ({ row }) => (
          <span className="num-cell text-sm">{formatPercent(row.data_completeness_rate)}</span>
        ),
      },
      {
        id: "perf_rate",
        header: "Perf rate",
        width: 88,
        align: "right",
        cell: ({ row }) => <span className="num-cell text-sm">{formatPercent(row.performance_rate)}</span>,
      },
      {
        id: "decile",
        header: "Decile",
        width: 72,
        align: "center",
        cell: ({ row }) =>
          hasSnapshot ? <DecileCell decile={row.decile} /> : <span className="text-gray-400">—</span>,
      },
      {
        id: "benchmark",
        header: `${year} bench`,
        width: 132,
        cell: ({ row }) => <BenchmarkCell row={row} />,
      },
      {
        id: "gap",
        header: "Gap to next decile",
        minWidth: 280,
        cell: ({ row }) => <GapAnalysisCell gap={row.gap_analysis} />,
      },
      {
        id: "points",
        header: "Pts",
        width: 56,
        align: "right",
        cell: ({ row }) =>
          hasSnapshot ? (
            <span className="num-cell text-sm font-semibold tnum">{formatPoints(row.points)}</span>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      {
        id: "goal",
        header: "Goal",
        minWidth: 180,
        cell: ({ row }) => (
          <GoalCell
            clinicId={clinicId}
            year={year}
            measureId={row.measure_id}
            currentGoal={row.current_goal ?? null}
            pinned={pinned}
          />
        ),
      },
    ],
    [clinicId, year, hasSnapshot, pinned],
  );

  const selectedCount = rollup.selected_count;

  return (
    <Widget title="Quality measures" subtitle={`${measures.length} active · ${selectedCount} selected for submit`}>
      <SchemaDataGrid<MeasureRow>
        rows={measures}
        columns={columns}
        getRowId={(r) => String(r.measure_id)}
        density="compact"
        emptyTitle="No active measures"
        emptyBody="This clinic has no Quality measures active for this performance year."
      />
      {/* Rollup footer row — handoff §3.6. Custom slot below the grid. */}
      {measures.length > 0 ? (
        <div className="flex items-center justify-between border-t border-border bg-table-header-bg px-3 py-2 text-xs">
          <div className="font-medium text-table-header-text">
            Selected: {selectedCount} measure{selectedCount === 1 ? "" : "s"}
          </div>
          <div className="flex gap-6">
            <div>
              <span className="text-gray-500">Σ deciles selected</span>{" "}
              <span className="font-semibold tnum">{rollup.selected_decile_total}</span>
            </div>
            <div>
              <span className="text-gray-500">Quality category pts</span>{" "}
              <span className="font-semibold tnum">{formatPoints(rollup.quality_category_points)}</span>
            </div>
          </div>
        </div>
      ) : null}
    </Widget>
  );
}
